import React, { useCallback, useState } from "react";
import classNames from "classnames";

import { LargeTextHeader } from "../../common/LargeTextHeader";
import { MorphSticker } from "../../common/MorphSticker";
import styles from "./Contact.module.scss";
import { DivideSection } from "../../common/DivideSection";
import { Title } from "../../common/Title";
import { Newsletter } from "../../Layout/Newsletter";
import { CreditsFooter } from "../../Layout/CreditsFooter";
import { timeout } from "../../../utils/utils";
import { CreativeMarkdown } from "../../common/CreativeMarkdown";
import { LegalFooter } from "../../Layout/LegalFooter";

export const serialize = (formData: FormData): Record<string, any> => {
  return [...formData.entries()].reduce((prev, curr) => {
    return {
      ...prev,
      [curr[0]]: curr[1],
    };
  }, {});
};

const Contact: React.FC<{
  content: string;
  form: boolean;
  information?: string;
}> = ({ content, form, information }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState<false | string>(false);

  const onSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const target = e.target as HTMLFormElement;
    const formData = new FormData(target);

    // console.log(serialize(formData));

    setSubmitting(true);
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(() => {
        setFormMessage("Message sent");
        target.reset();
      })
      .catch(error => {
        // setFormMessage(error.toString());
        setFormMessage("Unable to send message");
      })
      .finally(() => setSubmitting(false));
  }, []);

  return (
    <>
      <LargeTextHeader title="Contact" rightAlign className={styles.header}>
        {content}
      </LargeTextHeader>

      <MorphSticker
        startingLeft={0.8}
        startingTop={0.45}
        path="Performance"
        svgClassName={classNames(styles.sticker, styles.stickerOne)}
      />
      <MorphSticker
        startingLeft={0.2}
        startingTop={0.15}
        path="Radio"
        svgClassName={classNames(styles.sticker, styles.stickerTwo)}
      />
      <MorphSticker
        startingLeft={0.2}
        startingTop={0.8}
        path="Discussion"
        svgClassName={classNames(styles.sticker, styles.stickerThree)}
      />

      <section className={styles.row}>
        <div className={styles.left}>
          <Title size="large">Get In Touch</Title>
        </div>
        <div className={styles.right}>
          {information && (
            <div className={styles.information}>
              <CreativeMarkdown options={{ forceBlock: true }}>
                {information}
              </CreativeMarkdown>
            </div>
          )}
          {formMessage && <span className={styles.message}>{formMessage}</span>}
          {form && (
            <form
              className={styles.form}
              method="post"
              netlify-honeypot="bot-field"
              data-netlify="true"
              name="contact"
              onSubmit={onSubmit}
            >
              <input type="hidden" name="bot-field" />
              <input type="hidden" name="form-name" value="contact" />

              <div className={styles.formRow}>
                <div className={styles.column}>
                  <input
                    disabled={submitting}
                    type="text"
                    name="name"
                    placeholder="First & Last Name*"
                    autoComplete="name"
                    required
                  />
                </div>
                <div className={styles.column}>
                  <input
                    disabled={submitting}
                    type="tel"
                    name="subject"
                    placeholder="Subject*"
                    autoComplete="off"
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.column}>
                  <input
                    disabled={submitting}
                    type="text"
                    name="telephone"
                    placeholder="Telephone"
                    autoComplete="tel"
                  />
                </div>
                <div className={styles.column}>
                  <input
                    disabled={submitting}
                    type="email"
                    name="email"
                    placeholder="Email Address*"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={classNames(styles.column, styles.columnDouble)}>
                  <textarea
                    disabled={submitting}
                    name="message"
                    placeholder="Message*"
                    required
                    rows={8}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.column}>
                  <button type="submit" className="std-button color-primary">
                    Submit enquiry
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      <Newsletter />

      <CreditsFooter />

      <LegalFooter />
    </>
  );
};

export { Contact };
